import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import { useWhatsAppUnread } from '../../context/WhatsAppUnreadContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { WHATSAPP_INBOX_REPLY_CODE as REPLY_CODE } from '../../utils/whatsapp';
import { mergeConversations, mergeMessages } from './inboxMerge';

const POLL_MS = 20000;
const CONV_PAGE = 50;
const CONV_PAGE_MAX = 200; // backend caps `limit` at 200 (whatsapp_inbox.py); never ask for more
const MSG_PAGE = 200;
const TEMPLATE_REFUSALS = [REPLY_CODE.TEMPLATE_UNAVAILABLE, REPLY_CODE.INVALID_PARAMS, REPLY_CODE.UNSUPPORTED_TEMPLATE];

/**
 * All inbox state and side effects: the conversation list, the open thread, polling, read receipts
 * and sending a reply. Components stay presentational; this is the one place that talks to the
 * service and reconciles optimistic vs polled data.
 */
export default function useInbox() {
  const toast = useToast();

  const [conversations, setConversations] = useState(/** @type {any[] | null} */ (null));
  const [convLoading, setConvLoading] = useState(true);
  const [convError, setConvError] = useState('');
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);

  const [selectedPhone, setSelectedPhoneState] = useState(/** @type {string | null} */ (null));
  const [thread, setThread] = useState(/** @type {any | null} */ (null));
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sendDisabled, setSendDisabled] = useState(false);
  // A chat opened for a number that is not in the list yet: its typed contact name, kept so the
  // template panel can pre-fill the first name and send-template can store it.
  const [startedChat, setStartedChat] = useState(/** @type {{phone: string, name: string} | null} */ (null));

  const convTicket = useRef(0);
  // One ticket line per load kind: a background poll (or manual refresh) must never supersede - and
  // so never silently discard - the reset load a fresh `selectConversation` started, nor a
  // load-earlier request in flight, and vice versa. Only two requests of the SAME kind (e.g. two
  // polls, or two "load earlier" clicks) should ever compete; a phone change still drops everything
  // via `selectedPhoneRef` below, regardless of kind.
  const threadResetTicket = useRef(0);
  const threadPollTicket = useRef(0);
  const threadBeforeTicket = useRef(0);
  const selectedPhoneRef = useRef(/** @type {string | null} */ (null));
  const conversationsRef = useRef(/** @type {any[] | null} */ (null));
  const threadRef = useRef(/** @type {any | null} */ (null));
  const convPollFailed = useRef(false);
  const threadPollFailed = useRef(false);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  useEffect(() => {
    threadRef.current = thread;
  }, [thread]);

  // Unmounting supersedes anything still in flight: bump every ticket so a late reply is ignored.
  useEffect(
    () => () => {
      convTicket.current += 1;
      threadResetTicket.current += 1;
      threadPollTicket.current += 1;
      threadBeforeTicket.current += 1;
    },
    [],
  );

  const unread = useWhatsAppUnread();
  const refreshUnread = unread.refresh;
  const markReadNow = useCallback(
    (phone) => {
      setConversations((prev) => (prev ? prev.map((c) => (c.phone === phone ? { ...c, unread_count: 0 } : c)) : prev));
      // best effort: a failed mark-read never blocks reading; on success the nav badge catches up at once
      adminWhatsAppService
        .markRead(phone)
        .then(() => refreshUnread())
        .catch(() => {});
    },
    [refreshUnread],
  );

  const loadConversations = useCallback(
    async ({ silent = false, more = false } = {}) => {
      const ticket = ++convTicket.current;
      if (!silent) setConvLoading(true);
      if (more) setLoadingMoreConversations(true);
      try {
        const offset = more ? conversationsRef.current?.length || 0 : 0;
        // A plain (re)load always re-fetches from the top, so it must ask for at least as many rows
        // as are already on screen - otherwise a poll or Refresh would truncate a list grown by
        // "Load more" back down to one page.
        const limit = more
          ? CONV_PAGE
          : Math.min(CONV_PAGE_MAX, Math.max(CONV_PAGE, conversationsRef.current?.length || 0));
        const page = await adminWhatsAppService.listConversations({ limit, offset });
        if (ticket !== convTicket.current) return;
        setConversations((prev) => (more && prev ? mergeConversations(prev, page) : page));
        setHasMoreConversations(page.length === limit);
        setConvError('');
        convPollFailed.current = false;
      } catch (err) {
        if (ticket !== convTicket.current) return;
        if (silent) {
          if (!convPollFailed.current) toast.error(err.message);
          convPollFailed.current = true;
        } else {
          setConvError(err.message);
        }
      } finally {
        if (ticket === convTicket.current) {
          setConvLoading(false);
          setLoadingMoreConversations(false);
        }
      }
    },
    [toast],
  );

  const loadThread = useCallback(
    /** @param {string} phone @param {{ silent?: boolean, before?: string, reset?: boolean }} [opts] */
    async (phone, { silent = false, before, reset = false } = {}) => {
      // A poll tick (or manual Refresh) and the reset load a fresh selection just started are
      // different kinds of request and must not invalidate each other's ticket, or whichever
      // resolves second silently wins with nothing to show (see the bug this fixed). Load-earlier
      // gets its own line for the same reason.
      const ticketRef = before ? threadBeforeTicket : reset ? threadResetTicket : threadPollTicket;
      const ticket = ++ticketRef.current;
      if (!silent && !before) setThreadLoading(true);
      if (before) setLoadingEarlier(true);
      try {
        const data = await adminWhatsAppService.getConversation(phone, { limit: MSG_PAGE, before });
        if (ticket !== ticketRef.current || selectedPhoneRef.current !== phone) return;

        if (before) {
          const prev = threadRef.current;
          if (prev && prev.phone === phone) {
            setThread({
              ...prev,
              contact: data.contact,
              suppressed: data.suppressed,
              windowOpen: data.window_open,
              lastInboundAt: data.last_inbound_at,
              wholesalePartner: data.wholesale_partner || null,
              messages: mergeMessages(data.messages, prev.messages),
            });
          }
          setHasMoreMessages(data.messages.length === MSG_PAGE);
        } else if (reset) {
          setThread({
            phone,
            contact: data.contact,
            suppressed: data.suppressed,
            windowOpen: data.window_open,
            lastInboundAt: data.last_inbound_at,
            wholesalePartner: data.wholesale_partner || null,
            messages: data.messages,
          });
          setHasMoreMessages(data.messages.length === MSG_PAGE);
          markReadNow(phone);
        } else {
          const prev = threadRef.current;
          if (prev && prev.phone === phone) {
            const prevIds = new Set(prev.messages.map((m) => m.id));
            const newInbound = data.messages.some((m) => m.direction === 'in' && !prevIds.has(m.id));
            setThread({
              ...prev,
              contact: data.contact,
              suppressed: data.suppressed,
              windowOpen: data.window_open,
              lastInboundAt: data.last_inbound_at,
              wholesalePartner: data.wholesale_partner || null,
              messages: mergeMessages(prev.messages, data.messages),
            });
            if (newInbound && document.visibilityState === 'visible') markReadNow(phone);
          }
        }
        setThreadError('');
        threadPollFailed.current = false;
      } catch (err) {
        if (ticket !== ticketRef.current || selectedPhoneRef.current !== phone) return;
        if (silent) {
          if (!threadPollFailed.current) toast.error(err.message);
          threadPollFailed.current = true;
        } else {
          setThreadError(err.message);
          if (err.code === 'invalid_phone') toast.error(err.message);
        }
      } finally {
        if (ticket === ticketRef.current) {
          if (!before) setThreadLoading(false);
          if (before) setLoadingEarlier(false);
        }
      }
    },
    [toast, markReadNow],
  );

  // Initial load.
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Poll the list, and the open thread, every 20s while the tab is visible.
  useEffect(() => {
    let timer = null;
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const tick = () => {
      loadConversations({ silent: true });
      if (selectedPhoneRef.current) loadThread(selectedPhoneRef.current, { silent: true });
    };
    const start = () => {
      stop();
      timer = setInterval(tick, POLL_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        stop();
        return;
      }
      tick();
      start();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadConversations, loadThread]);

  const selectConversation = useCallback(
    (phone) => {
      if (phone === selectedPhoneRef.current) return;
      selectedPhoneRef.current = phone;
      setSelectedPhoneState(phone);
      setThread(null);
      setThreadError('');
      setHasMoreMessages(false);
      loadThread(phone, { reset: true });
    },
    [loadThread],
  );

  const startChat = useCallback(
    (phone, displayName) => {
      const known = (conversationsRef.current || []).some((c) => c.phone === phone);
      setStartedChat(known ? null : { phone, name: String(displayName || '').trim() });
      selectConversation(phone);
    },
    [selectConversation],
  );

  const closeThread = useCallback(() => {
    selectedPhoneRef.current = null;
    setSelectedPhoneState(null);
    setThread(null);
  }, []);

  // Shared by sendReply and sendMedia: window_closed / suppressed / send_disabled flip the bar state;
  // anything else (including invalid_media, caption_not_allowed, text_too_long) is returned as a plain message.
  // `merge` lets a caller fold a successful send into the thread some other way than appending it as
  // a new row - sendReaction needs this (see below): a reaction is never its own message.
  const sendWith = useCallback(async (send, forPhone, merge) => {
    const phone = forPhone || selectedPhoneRef.current;
    if (!phone) return { ok: false, code: null, message: 'No conversation is open' };
    try {
      const message = await send(phone);
      setThread((prev) => {
        if (!prev || prev.phone !== phone) return prev;
        return merge ? merge(prev) : { ...prev, messages: mergeMessages(prev.messages, [message]) };
      });
      return { ok: true, message };
    } catch (err) {
      if (err.code === REPLY_CODE.WINDOW_CLOSED) {
        setThread((prev) => (prev && prev.phone === phone ? { ...prev, windowOpen: false } : prev));
        return { ok: false, code: REPLY_CODE.WINDOW_CLOSED, message: err.message };
      }
      if (err.code === REPLY_CODE.SUPPRESSED) {
        setThread((prev) => (prev && prev.phone === phone ? { ...prev, suppressed: true } : prev));
        return { ok: false, code: REPLY_CODE.SUPPRESSED, message: err.message };
      }
      if (err.code === REPLY_CODE.SEND_DISABLED) {
        setSendDisabled(true);
        return { ok: false, code: REPLY_CODE.SEND_DISABLED, message: err.message };
      }
      if (err.code === REPLY_CODE.WINDOW_OPEN) {
        setThread((prev) => (prev && prev.phone === phone ? { ...prev, windowOpen: true } : prev));
        return { ok: false, code: REPLY_CODE.WINDOW_OPEN, message: err.message };
      }
      if (TEMPLATE_REFUSALS.includes(err.code)) return { ok: false, code: err.code, message: err.message };
      return { ok: false, code: null, message: err.message };
    }
  }, []);

  const sendReply = useCallback(
    (text, replyToMessageId) => sendWith((phone) => adminWhatsAppService.reply(phone, text, replyToMessageId)),
    [sendWith],
  );
  const sendMedia = useCallback(
    // `forPhone` is the conversation the admin pressed Send in; a slow upload must not follow them to another chat.
    (body, forPhone) => sendWith((phone) => adminWhatsAppService.replyMedia(phone, body), forPhone),
    [sendWith],
  );
  // A reaction is never shown as its own chat row (real WhatsApp never does that either) - patch the
  // badge straight onto the target message instead of appending a fake "Reacted X" message, so there
  // is never a flash of a standalone bubble before the next poll cleans it up.
  const sendReaction = useCallback(
    (targetMessageId, emoji) =>
      sendWith(
        (phone) => adminWhatsAppService.react(phone, targetMessageId, emoji),
        undefined,
        (prev) => ({
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== targetMessageId) return m;
            const next = { ...(m.reactions || {}) };
            if (emoji) next.out = emoji;
            else delete next.out;
            return { ...m, reactions: Object.keys(next).length ? next : null };
          }),
        }),
      ),
    [sendWith],
  );
  const sendInteractive = useCallback(
    (payload) => sendWith((phone) => adminWhatsAppService.sendInteractive(phone, payload)),
    [sendWith],
  );
  const sendCatalog = useCallback(
    (body) => sendWith((phone) => adminWhatsAppService.sendCatalog(phone, body)),
    [sendWith],
  );

  const editMessage = useCallback(async (messageId, text) => {
    const phone = selectedPhoneRef.current;
    if (!phone) return { ok: false, code: null, message: 'No conversation is open' };
    try {
      const result = await adminWhatsAppService.editMessage(phone, messageId, text);
      setThread((prev) =>
        prev && prev.phone === phone
          ? {
              ...prev,
              messages: prev.messages.map((m) =>
                m.id === messageId ? { ...m, text: result.text, edit_text: result.text } : m,
              ),
            }
          : prev,
      );
      return { ok: true };
    } catch (err) {
      return { ok: false, code: err.code || null, message: err.message };
    }
  }, []);

  const deleteMessage = useCallback(
    async (messageId) => {
      const phone = selectedPhoneRef.current;
      if (!phone) return;
      try {
        const result = await adminWhatsAppService.deleteMessage(phone, messageId);
        setThread((prev) => {
          if (!prev || prev.phone !== phone) return prev;
          if (result.removed) {
            return { ...prev, messages: prev.messages.filter((m) => m.id !== messageId) };
          }
          return {
            ...prev,
            messages: prev.messages.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    text: '🚫 This message was deleted',
                    deleted: true,
                    media_url: null,
                    media_pending: false,
                    reactions: null,
                  }
                : m,
            ),
          };
        });
      } catch (err) {
        toast.error(err.message || 'Could not delete that message');
      }
    },
    [toast],
  );

  const sendTemplate = useCallback(
    async ({ template_id, body_params }) => {
      const name = startedChat && startedChat.phone === selectedPhoneRef.current ? startedChat.name : '';
      const result = await sendWith((phone) =>
        adminWhatsAppService.sendTemplate(phone, {
          template_id,
          body_params,
          ...(name && { display_name: name }),
        }),
      );
      if (result.ok) loadConversations({ silent: true }); // a brand-new chat now has a row
      return result;
    },
    [sendWith, startedChat, loadConversations],
  );

  const refresh = useCallback(() => {
    loadConversations();
    if (selectedPhoneRef.current) loadThread(selectedPhoneRef.current, {});
  }, [loadConversations, loadThread]);

  // Lazy media (image/video/audio/document/sticker a customer sent): nothing is downloaded until
  // the admin taps it. Patches the thread in place for an instant update; the next poll would pick
  // up the now-persisted media_url anyway, but that can be up to POLL_MS away.
  const fetchMedia = useCallback(async (messageId) => {
    const { url, mime_type: mimeType } = await adminWhatsAppService.fetchInboundMedia(messageId);
    const prev = threadRef.current;
    if (prev) {
      setThread({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, media_url: url, media_mime_type: mimeType, media_pending: false } : m,
        ),
      });
    }
    return { url, mime_type: mimeType };
  }, []);

  return {
    conversations: conversations || [],
    conversationsLoaded: conversations !== null,
    convLoading,
    convError,
    hasMoreConversations,
    loadingMoreConversations,
    loadMoreConversations: () => loadConversations({ more: true }),
    retryConversations: () => loadConversations(),

    selectedPhone,
    selectConversation,
    startChat,
    startedChat,
    closeThread,

    thread,
    threadLoading,
    threadError,
    hasMoreMessages,
    loadingEarlier,
    loadEarlierMessages: () => {
      const phone = selectedPhoneRef.current;
      const oldest = threadRef.current?.messages?.[0];
      if (phone && oldest) loadThread(phone, { before: oldest.at });
    },
    retryThread: () => {
      const phone = selectedPhoneRef.current;
      if (phone) loadThread(phone, { reset: true });
    },

    sendReply,
    sendMedia,
    sendReaction,
    sendInteractive,
    sendCatalog,
    sendTemplate,
    editMessage,
    deleteMessage,
    sendDisabled,
    refresh,
    fetchMedia,
  };
}
